import React, { useEffect, useState, useCallback, useRef } from "react";
import { Routes, Route, useLocation } from "react-router-dom";
import { toast } from "sonner";
import RequireLawyer from "@lawyer/components/RequireLawyer";
import LawyerLayout from "@lawyer/layouts/LawyerLayout";
import LawyerLogin from "@lawyer/pages/LawyerLogin";
import LawyerDashboard from "@lawyer/pages/LawyerDashboard";
import LawyerConsultations from "@lawyer/pages/LawyerConsultations";
import LawyerEarnings from "@lawyer/pages/LawyerEarnings";
import LawyerAvailability from "@lawyer/pages/LawyerAvailability";
import LawyerProfile from "@lawyer/pages/LawyerProfile";
import LawyerReviews from "@lawyer/pages/LawyerReviews";
import LawyerKyc from "@lawyer/pages/LawyerKyc";
import LawyerBarCouncil from "@lawyer/pages/LawyerBarCouncil";
import LawyerLCR from "@lawyer/pages/LawyerLCR";
import LawyerPricing from "@lawyer/pages/LawyerPricing";
import { fetchAvailabilityStatus, fetchDashboard, setAvailability } from "@lawyer/services/lawyerApi";
import { NewBookingAlert } from "@lawyer/components/NewBookingAlert";
import ConsultationRoom from "@shared/consultation/ConsultationRoom";
import { useAuth } from "@/context/AuthContext";
import { photoSrc } from "@shared/photoSrc";
import {
  AVAILABILITY_REFRESH_EVENT,
  DASHBOARD_RELOAD_EVENT as NEW_BOOKING_EVENT,
  requestAvailabilityRefresh,
  requestDashboardReload,
} from "@lawyer/constants/events";

/**
 * Lawyer Portal — mounted at REACT_APP_LAWYER_PATH (default /lawyer-portal).
 *
 * The shell loads the dashboard payload once and shares it with all child
 * pages via props/context-on-the-fly to avoid duplicate /api/lawyer/dashboard
 * calls. Child pages can call `reload()` to refresh after mutations.
 */
export function LawyerShell() {
  const [data, setData] = useState(null);
  const location = useLocation();
  const requestVersionRef = useRef(0);
  const lastFocusRefreshRef = useRef(0);

  const reload = useCallback(async () => {
    const requestVersion = ++requestVersionRef.current;
    try {
      const dashboard = await fetchDashboard();
      if (requestVersion === requestVersionRef.current) setData(dashboard);
    } catch (e) {
      if (requestVersion === requestVersionRef.current) toast.error(e?.response?.data?.detail || "Could not load dashboard");
    }
  }, []);

  const refreshAvailability = useCallback(async ({ force = false } = {}) => {
    const now = Date.now();
    if (!force && now - lastFocusRefreshRef.current < 1000) return;
    lastFocusRefreshRef.current = now;
    const requestVersion = ++requestVersionRef.current;
    try {
      const availability = await fetchAvailabilityStatus();
      if (requestVersion !== requestVersionRef.current) return;
      setData((prev) => prev ? {
        ...prev,
        lawyer: {
          ...prev.lawyer,
          available: availability.instant_available,
          availability,
          vacation_mode: availability.vacation_mode,
        },
      } : prev);
    } catch (e) {
      if (requestVersion === requestVersionRef.current) {
        toast.error(e?.response?.data?.detail || "Could not refresh availability");
      }
    }
  }, []);

  useEffect(() => { reload(); }, [reload]);
  useEffect(() => {
    window.addEventListener(NEW_BOOKING_EVENT, reload);
    return () => window.removeEventListener(NEW_BOOKING_EVENT, reload);
  }, [reload]);
  useEffect(() => {
    const refreshAfterCompletion = () => { refreshAvailability({ force: true }); };
    const refreshWhenVisible = () => {
      if (document.visibilityState !== "hidden") refreshAvailability();
    };
    window.addEventListener(AVAILABILITY_REFRESH_EVENT, refreshAfterCompletion);
    window.addEventListener("focus", refreshWhenVisible);
    document.addEventListener("visibilitychange", refreshWhenVisible);
    return () => {
      window.removeEventListener(AVAILABILITY_REFRESH_EVENT, refreshAfterCompletion);
      window.removeEventListener("focus", refreshWhenVisible);
      document.removeEventListener("visibilitychange", refreshWhenVisible);
    };
  }, [refreshAvailability]);

  const onAvailabilityToggle = async (e) => {
    if (!data) return;
    const wasAvailable = !!data.lawyer.available;
    // "Stay online" re-sends true to extend the 30-min manual window.
    const extend = e?.currentTarget?.dataset?.action === "extend";
    const next = extend ? true : data.lawyer.availability?.state !== "online_manual";
    // Optimistic flip BEFORE the network round-trip so the sidebar pill
    // updates within a single render frame.
    setData((prev) => prev ? {
      ...prev,
      lawyer: { ...prev.lawyer, available: next, availability: { ...(prev.lawyer.availability || {}), state: next ? "online_manual" : "offline" } },
    } : prev);
    try {
      const updated = await setAvailability({ available: next });
      // Reconcile with authoritative server response (e.g. vacation_mode
      // may have forced available=false).
      setData((prev) => prev ? {
        ...prev,
        lawyer: { ...prev.lawyer,
          available: updated.available,
          availability: updated.availability,
          vacation_mode: updated.vacation_mode,
          vacation_until: updated.vacation_until,
        },
      } : prev);
      toast.success(extend ? "Online for another 30 minutes" : `You are now ${updated.available ? "online" : "offline"}`);
      reload();
    } catch (e) {
      // Roll back optimistic update.
      setData((prev) => prev ? {
        ...prev,
        lawyer: { ...prev.lawyer, available: wasAvailable },
      } : prev);
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  const onVacationToggle = async () => {
    if (!data) return;
    const wasVacation = !!data.lawyer.vacation_mode;
    const next = !wasVacation;
    setData((prev) => prev ? {
      ...prev,
      lawyer: { ...prev.lawyer,
        vacation_mode: next,
        // Vacation forces offline.
        available: next ? false : prev.lawyer.available,
        availability: { ...(prev.lawyer.availability || {}), state: next ? "vacation" : "offline" },
      },
    } : prev);
    try {
      const updated = await setAvailability({ vacation_mode: next });
      setData((prev) => prev ? {
        ...prev,
        lawyer: { ...prev.lawyer,
          available: updated.available,
          availability: updated.availability,
          vacation_mode: updated.vacation_mode,
          vacation_until: updated.vacation_until,
        },
      } : prev);
      if (updated.vacation_mode && updated.upcoming_bookings_count > 0) {
        // Existing paid bookings are never cancelled automatically — the lawyer must handle them.
        toast.warning(`Vacation mode on. You still have ${updated.upcoming_bookings_count} upcoming paid consultation${updated.upcoming_bookings_count > 1 ? "s" : ""} — they remain booked. Attend or cancel them from Consultations.`, { duration: 9000 });
      } else {
        toast.success(updated.vacation_mode ? "Vacation mode on — you're offline" : "Welcome back");
      }
      reload();
    } catch (e) {
      setData((prev) => prev ? {
        ...prev,
        lawyer: { ...prev.lawyer, vacation_mode: wasVacation },
      } : prev);
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  return (
    <LawyerLayout
      available={data?.lawyer?.available || false}
      vacationMode={data?.lawyer?.vacation_mode || false}
      listed={data ? !!data.lawyer?.listed : null}
      availability={data?.lawyer?.availability || null}
      vacationUntil={data?.lawyer?.vacation_until || null}
      alertCount={data?.counts?.upcoming || 0}
      photo={photoSrc(data?.lawyer?.photo, "sm")}
      onAvailabilityToggle={onAvailabilityToggle}
      onVacationToggle={onVacationToggle}
    >
      <Routes>
        <Route index element={<LawyerDashboard data={data} onReload={reload} onAvailabilityToggle={onAvailabilityToggle} onVacationToggle={onVacationToggle} />} />
        <Route path="consultations" element={<LawyerConsultations counts={data?.counts} />} />
        <Route path="consultations/:cid/lcr" element={<LawyerLCR />} />
        <Route path="availability" element={<LawyerAvailability data={data} onReload={reload} onVacationToggle={onVacationToggle} />} />
        <Route path="earnings" element={<LawyerEarnings />} />
        <Route path="pricing" element={<LawyerPricing />} />
        <Route path="reviews" element={<LawyerReviews />} />
        <Route path="kyc" element={<LawyerKyc />} />
        <Route path="bar-council" element={<LawyerBarCouncil />} />
        <Route path="profile" element={<LawyerProfile />} />
      </Routes>
    </LawyerLayout>
  );
}

// Booking popup lives at the app root so it reaches every logged-in lawyer
// screen (dashboard, consultation room, LCR…) — not just the portal shell.
function LawyerBookingAlerts() {
  const { user } = useAuth();
  const onSeen = useCallback(requestDashboardReload, []);
  const onAvailabilityChanged = useCallback(requestAvailabilityRefresh, []);
  if (!user || user.role !== "lawyer" || !user.lawyer_id) return null;
  return <NewBookingAlert lawyerId={user.lawyer_id} enabled onSeen={onSeen} onAvailabilityChanged={onAvailabilityChanged} />;
}

export default function LawyerApp() {
  return (
    <>
      <LawyerBookingAlerts />
      <Routes>
        <Route path="login" element={<LawyerLogin />} />
        {/* Chat/call room under the lawyer scope — uses the lawyer session, no portal sidebar. */}
        <Route path="consultations/:id/room" element={<RequireLawyer><ConsultationRoom /></RequireLawyer>} />
        <Route path="*" element={
          <RequireLawyer>
            <LawyerShell />
          </RequireLawyer>
        } />
      </Routes>
    </>
  );
}
